import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FhMenuModel } from '../helpers/objects/fh-menu-model';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FhMenuProviderService } from '../helpers/providers/fh-menu-provider.service';
import { SharedFeaturesService } from '@fh-client-interaction-frontends/fh-providers';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { SharedSettingsService } from '@fh-client-interaction-frontends/fh-configurations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PlatformEnum } from '@fh-client-interaction-frontends/fh-enums';
import { CpContractsService } from '@fh-client-interaction-frontends/fh-client-portal-service';

@Component({
  selector: 'ui-fh-menu',
  templateUrl: './fh-menu.component.html',
  styleUrls: ['./fh-menu.component.scss']
})
export class FhMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() items: Array<FhMenuModel> = [];
  @Input() header: boolean = true;
  @Input() expandable: boolean = true;
  @Input() sticky: boolean = true;
  @Input() validateFeatures: boolean = true;
  @Output() menuExpandedChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() isMenuExpanded: boolean = true;

  appConfig: EnvironmentModel = new EnvironmentModel();
  dropdownToggleChangedSubscription: Subscription = new Subscription();
  activeToggleChangedSubscription: Subscription = new Subscription();
  stateMenuSubscription: Subscription = new Subscription();
  routerSubscription: Subscription = new Subscription();
  featuresUpdatedSubscription: Subscription = new Subscription();
  icon = '';
  nightMode: boolean = false;
  observer: MutationObserver | null = null;

  constructor(
    @Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel,
    private router: Router,
    private fhMenuProviderService: FhMenuProviderService,
    private sharedSettingsService: SharedSettingsService,
    private sharedFeaturesService: SharedFeaturesService,
    private breakpointObserver: BreakpointObserver,
    private cpContractsService: CpContractsService
  ) {
    this.appConfig = appConfigTemp;
    this.icon = this.appConfig.icon;
  }

  ngOnInit() {
    this.setItemActiveWithRoute(this.items, this.router.url);
    this.backgroundEvents();
    this.getLocalSettings();

    this.items.filter(row => (row.icon && !row.icon.startsWith(this.icon))).forEach((item: FhMenuModel) => item.icon = this.icon + item.icon);

    this.featuresUpdatedSubscription = this.sharedFeaturesService.updated.subscribe({
      next: () => this.initData()
    });
  }

  ngAfterViewInit() {
    this.observeWalkthroughPopUp();
  }

  ngOnDestroy() {
    this.dropdownToggleChangedSubscription.unsubscribe();
    this.activeToggleChangedSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
    this.stateMenuSubscription.unsubscribe();
    this.featuresUpdatedSubscription.unsubscribe();
  }

  backgroundEvents() {
    this.dropdownToggleChangedSubscription = this.fhMenuProviderService.dropdownToggleChanged.subscribe({
      next: () => this.updateDropdownOpenStatus(this.items)
    });

    this.activeToggleChangedSubscription = this.fhMenuProviderService.activeToggleChanged.subscribe({
      next: (item: FhMenuModel) => {
        this.updateActiveStatus(item, this.items);
      }
    });

    this.stateMenuSubscription = this.fhMenuProviderService.stateMenu.subscribe({
      next: (response: boolean) => {
        if (this.header) {
          this.menuToggleExpanded(response);
        }
      }
    });
    this.routerSubscription = this.router.events.subscribe((val: any) => {
      if (val.routerEvent && val.routerEvent.type && (val.routerEvent.type === 1 || val.routerEvent.type === 16)) {
        this.setItemActiveWithRoute(this.items, val.routerEvent.urlAfterRedirects);
      }
    });
  }

  getLocalSettings() {
    this.initData();
    this.fhMenuProviderService.isMenuExpanded = this.isMenuExpanded;
    this.sharedSettingsService.get().subscribe({
      next: (value: any) => {
        this.isMenuExpanded = value?.expandedMenu;
        this.nightMode = value?.nightMode;
        this.controlMenuStates(value);
      }
    });
  }

  controlMenuStates(value: any) {
    this.breakpointObserver.observe(['(max-width: 1024px)']).subscribe((result) => {
      if (result.matches && value?.expandedMenu === 'automatic') {
        setTimeout(() => {
          this.fhMenuProviderService.stateMenu.emit(false);
        });
      } else if (!result.matches && value?.expandedMenu === 'automatic') {
        setTimeout(() => {
          this.fhMenuProviderService.stateMenu.emit(true);
        });
      } else {
        switch (value?.expandedMenu) {
          case 'true':
          case 'false':
            setTimeout(() => {
              this.fhMenuProviderService.stateMenu.emit(value?.expandedMenu === 'true');
            });
            break;
        }
      }
    });
  }

  private async initData() {
    if (!this.validateFeatures) {
      return;
    }

    let token: string | undefined;

    if (this.appConfig.platform == PlatformEnum.clientPortal) {
      token = this.cpContractsService.contract?.Token;
    }

    for (const item of this.items) {
      item.visible = await this.sharedFeaturesService.exists(item.tag, item.parentTag, token);
    }

    this.items = [...this.items];
  }

  setItemActiveWithRoute(items: FhMenuModel[], url: string): void {
    items.forEach((item) => {
      this.checkItemRoute(item, url);

      if (item.childs) {
        this.setItemActiveWithRoute(item.childs, url);
      }
    });
  }

  checkItemRoute(item: FhMenuModel, url: string) {
    if (item.link && !item.childs && url && url.includes(item.link)) {
      this.fhMenuProviderService.activeToggleChanged.emit(item);
      item.active = true;
    }
  }

  menuToggleExpanded(expanded?: boolean) {
    if (expanded != undefined) {
      this.fhMenuProviderService.isMenuExpanded = expanded;
      this.isMenuExpanded = expanded;
    } else {
      this.fhMenuProviderService.isMenuExpanded = !this.fhMenuProviderService.isMenuExpanded;
      this.isMenuExpanded = this.fhMenuProviderService.isMenuExpanded;
    }

    this.fhMenuProviderService.menuToggleExpanded.emit(true);
    this.menuExpandedChanged.emit();
  }

  updateDropdownOpenStatus(items: FhMenuModel[]): void {
    items.forEach((item) => {
      item.dropdownOpen = false;

      if (Array.isArray(item.childs)) {
        this.updateDropdownOpenStatus(item.childs);
      }
    });
  }

  updateActiveStatus(itemChanged: FhMenuModel, items: FhMenuModel[]): void {
    items.forEach((item) => {
      item.active = itemChanged.parentTag === item.tag && item.tag != '';
      if (item.parentItem && !this.isMenuExpanded) {
        item.parentItem.active = (itemChanged.parentTag === item.parentTag && item.parentTag != '') || (itemChanged.parentTag === item.tag && item.tag != '');
      }
      if (Array.isArray(item.childs)) {
        this.updateActiveStatus(itemChanged, item.childs);
      }
    });
  }

  private observeWalkthroughPopUp(): void {
    this.observer = new MutationObserver(() => {
      const walkthroughPopUp = document.getElementsByClassName('walkthrough-popUp').length;

      if (walkthroughPopUp > 0) {
        this.updateMenuZIndex(0);
      } else {
        this.updateMenuZIndex(1);
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private updateMenuZIndex(zIndex: number): void {
    const menu = document.getElementById('menu');
    if (menu) {
      menu.style.zIndex = zIndex.toString();
    }
  }
}
